// =============================================================================
// BP-07-03: RelatedResources Component
// =============================================================================

import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { ResourceCard } from './ResourceCard';
import type { Resource } from '@/features/resources/types/resourcesTypes';
import { cn } from '@/core/lib/utils';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

interface RelatedResourcesProps {
  resources: Resource[];
  title?: string;
  viewAllLink?: string;
  variant?: 'grid' | 'list' | 'compact';
  maxItems?: number;
  className?: string;
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function RelatedResources({
  resources,
  title = 'Related Resources',
  viewAllLink,
  variant = 'list',
  maxItems = 4,
  className,
}: RelatedResourcesProps) {
  if (resources.length === 0) return null;

  const displayResources = resources.slice(0, maxItems);

  return (
    <section className={cn('', className)}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display font-medium text-lg text-navy">{title}</h3>
        {viewAllLink && (
          <Link
            to={viewAllLink}
            className="text-sm text-sage hover:text-sage-dark flex items-center gap-1 transition-colors"
          >
            View all
            <ArrowRight className="w-4 h-4" />
          </Link>
        )}
      </div>

      {/* Grid variant */}
      {variant === 'grid' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {displayResources.map((resource) => (
            <ResourceCard
              key={resource.id}
              resource={resource}
              variant="default"
              showCategory={false}
            />
          ))}
        </div>
      )}

      {/* List variant */}
      {variant === 'list' && (
        <div className="space-y-3">
          {displayResources.map((resource) => (
            <ResourceCard
              key={resource.id}
              resource={resource}
              variant="horizontal"
              showCategory={false}
            />
          ))}
        </div>
      )}

      {/* Compact variant */}
      {variant === 'compact' && (
        <div className="space-y-2">
          {displayResources.map((resource) => (
            <ResourceCard
              key={resource.id}
              resource={resource}
              variant="compact"
            />
          ))}
        </div>
      )}

      {/* Show more indicator */}
      {resources.length > maxItems && viewAllLink && (
        <div className="mt-4 text-center">
          <Link
            to={viewAllLink}
            className="text-sm text-warm-gray hover:text-sage transition-colors"
          >
            +{resources.length - maxItems} more resources
          </Link>
        </div>
      )}
    </section>
  );
}

// -----------------------------------------------------------------------------
// Sidebar Variant
// -----------------------------------------------------------------------------

interface RelatedResourcesSidebarProps {
  resources: Resource[];
  title?: string;
  className?: string;
}

export function RelatedResourcesSidebar({
  resources,
  title = 'Related',
  className,
}: RelatedResourcesSidebarProps) {
  if (resources.length === 0) return null;

  return (
    <aside className={cn('bg-white rounded-xl border border-warm-gray/10 p-4', className)}>
      <h3 className="font-display font-medium text-navy mb-4">{title}</h3>
      <div className="space-y-3">
        {resources.slice(0, 5).map((resource) => (
          <Link
            key={resource.id}
            to={`/resources/${resource.slug}`}
            className="block group"
          >
            <h4 className="text-sm font-medium text-navy group-hover:text-sage-dark line-clamp-2 transition-colors">
              {resource.title}
            </h4>
            {resource.excerpt && (
              <p className="text-xs text-warm-gray mt-1 line-clamp-2">
                {resource.excerpt}
              </p>
            )}
          </Link>
        ))}
      </div>
    </aside>
  );
}

export default RelatedResources;

